/**
 * Maps backend alertCodes to task card icons.
 *
 * Should cover every alertCode in ALERT_TASK_CONFIG in
 * backend/src/v2/internalApi/operational-dashboard/config/alert-task-config.ts.
 *
 * Entries for codes that were *removed* from ALERT_TASK_CONFIG (depthTooDeep,
 * depthTooShallow, bigVerticalDisparity, calibrationAge, uptime — dropped in
 * WB-1991) are intentionally retained so historical activity_log rows still
 * render with the correct icon. Falls back to Warning on miss.
 */

import type { FunctionComponent, SVGProps } from 'react';

import AdjustCameraAngle from '@/assets/icons/operational-dashboard/adjust-camera-angle.svg?component';
import CleanLenses from '@/assets/icons/operational-dashboard/clean-lenses.svg?component';
import MoveBioscope from '@/assets/icons/operational-dashboard/move-bioscope.svg?component';
import RestartBioscope from '@/assets/icons/operational-dashboard/restart-bioscope.svg?component';
import Warning from '@/assets/icons/warning.svg?component';

type SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

const ALERT_CODE_ICON: Record<string, SvgComponent> = {
    // Connectivity — power/restart
    noMeasurements1d: RestartBioscope,
    noMeasurements2h: RestartBioscope,

    // Placement & repositioning — camera angle
    badAngle: AdjustCameraAngle,
    depthMissing: AdjustCameraAngle,
    visibility_net: AdjustCameraAngle,

    // Data quality — move bioscope
    fewMeas: MoveBioscope,

    // Image quality / cleaning — brush
    visibility_rope: CleanLenses,
    visibility_dew: CleanLenses,

    // Historical (removed from ALERT_TASK_CONFIG in WB-1991, kept for
    // backward-compat with existing activity_log rows).
    depthTooDeep: AdjustCameraAngle,
    depthTooShallow: AdjustCameraAngle,
    bigVerticalDisparity: MoveBioscope,
    calibrationAge: MoveBioscope,
    uptime: MoveBioscope,
};

export function getTaskIcon(alertCode: string | null): SvgComponent {
    if (alertCode && ALERT_CODE_ICON[alertCode]) {
        return ALERT_CODE_ICON[alertCode];
    }
    return Warning;
}
